import { Title } from '@solidjs/meta';
import { createSignal, createEffect, Show, children } from 'solid-js';
import { Button, Heading, Section, Dialog, DialogContent, AccessLogo } from '@troon/ui';
import { IconArrowRightMd } from '@troon/icons/arrow-right-md';
import { useLocation, useSearchParams, useNavigate, createAsync, redirect, query } from '@solidjs/router';
import { IconArrowLeftMd } from '@troon/icons/arrow-left-md';
import { IconInfo } from '@troon/icons/info';
import { cachedGet } from '@troon/api-client';
import { getLoggedInUser } from '../../providers/user';
import { DefaultRightContent } from '../../components/points-transfer/default-right-content';
import { TierProgression } from '../../components/points-transfer/tier-progression';
import { ParticipatingCoursesMap } from '../../components/points-transfer/participating-courses-map';
import { ConvertPoints } from '../../components/points-transfer/convert-points';
import { FrequentlyAskedQuestions } from '../../components/faqs';
import type { Facility } from '@troon/api-client';
import type { RouteDefinition } from '@solidjs/router';
import type { JSX } from 'solid-js';

type StepConfig = {
	title?: string;
	leftContent: (props?: {
		confirmDialogOpen?: boolean;
		setConfirmDialogOpen?: (open: boolean) => void;
		facilities?: Array<Facility>;
		selectedOption?: 'primary' | 'upgrade' | null;
		setSelectedOption?: (option: 'primary' | 'upgrade' | null) => void;
		setConversionComplete?: (complete: boolean) => void;
	}) => JSX.Element;
	rightContent?: (props: { facilities?: Array<Facility> }) => JSX.Element;
	dialogTitle?: string;
	fullWidth?: boolean;
};

const STEPS: Array<StepConfig> = [
	{
		title: 'Welcome to Access –',
		leftContent: () => (
			<p class="text-3xl font-bold md:text-4xl">
				The next-generation loyalty program that rewards every round you play and unlocks the ultimate flexibility in
				redemption.
			</p>
		),
		rightContent: () => <DefaultRightContent />,
	},
	{
		title: 'Unparalleled flexibility –',
		leftContent: () => (
			<p class="text-3xl font-bold md:text-4xl">
				No more phone calls. No more redemption table. Fewer restrictions. Seamlessly book redemption rounds with points
				online or in the mobile app.
			</p>
		),
		rightContent: () => <DefaultRightContent />,
	},
	{
		title: 'The more you play, the more you earn –',
		leftContent: () => (
			<p class="text-3xl font-bold md:text-4xl">
				Earn 1 Access Point for every dollar spent. Unlock point multipliers as you level-up your status.
			</p>
		),
		rightContent: () => (
			<div class="flex h-full items-start justify-center bg-neutral-100 px-6 py-8 md:items-center md:px-12 md:py-12">
				<div class="w-full max-w-lg">
					<TierProgression />
				</div>
			</div>
		),
		dialogTitle: 'Status Levels',
	},
	{
		title: 'Play when you want, where you want –',
		leftContent: () => (
			<div class="flex flex-col gap-6">
				<p class="text-3xl font-bold md:text-4xl">
					Use your Access Points to book any eligible tee time at participating courses.
				</p>
				<p class="text-sm text-black">
					Not all Troon Rewards courses are participating in Access yet. More courses are being added daily.
				</p>
			</div>
		),
		rightContent: (props) => <ParticipatingCoursesMap facilities={props.facilities} />,
		dialogTitle: 'Participating Courses',
	},
	{
		title: 'Convert your points today –',
		leftContent: () => (
			<>
				<p class="text-3xl font-bold md:text-4xl">
					You have until <span class="text-brand">February 10, 2026</span> to convert your Troon Rewards points.
				</p>
				<div class="mt-4 grid gap-2 md:gap-4">
					<Heading as="h3" size="h5">
						Frequently Asked Questions
					</Heading>
					<div class="rounded-lg border border-neutral">
						<FrequentlyAskedQuestions id="access-rewards-conversion-flow-faqs" />
					</div>
				</div>
			</>
		),
		rightContent: () => <DefaultRightContent />,
	},
	{
		title: '',
		leftContent: (props) => (
			<ConvertPoints
				confirmDialogOpen={props?.confirmDialogOpen}
				setConfirmDialogOpen={props?.setConfirmDialogOpen}
				selectedOption={props?.selectedOption}
				setSelectedOption={props?.setSelectedOption}
				onConversionComplete={() => props?.setConversionComplete?.(true)}
			/>
		),
		fullWidth: true,
	},
];

const TOTAL_STEPS = STEPS.length;

// Redirect users with 0 Troon Rewards points to opt into Access Rewards
const redirectZeroPointsUsers = query(async () => {
	const user = await getLoggedInUser();
	if (user?.supportsTroonRewards && (user.rewardsPoints ?? 0) === 0) {
		return redirect('/account?openAccessOptIn=true');
	}
	return user;
}, 'redirect-zero-points-users');

export default function TransferPoints() {
	const [searchParams] = useSearchParams<{ step?: string }>();
	const navigate = useNavigate();
	const location = useLocation();

	const initialStep = (() => {
		const step = parseInt(searchParams.step || '1', 10);
		return Math.max(1, Math.min(step, TOTAL_STEPS));
	})();
	const [currentStep, setCurrentStep] = createSignal(initialStep);
	const [confirmDialogOpen, setConfirmDialogOpen] = createSignal(false);
	const [selectedOption, setSelectedOption] = createSignal<'primary' | 'upgrade' | null>(null);
	const [conversionComplete, setConversionComplete] = createSignal(false);

	const getReturnUrl = (): string => {
		const redirect = (location.state as { redirect?: string } | undefined)?.redirect;
		if (redirect && !redirect.includes('/account/transfer-points')) {
			return redirect;
		}
		return '/account';
	};

	createEffect(() => {
		const step = parseInt(searchParams.step || '1', 10);
		const validStep = Math.max(1, Math.min(step, TOTAL_STEPS));
		if (currentStep() !== validStep) {
			setCurrentStep(validStep);
		}
	});

	const goToStep = (step: number) => {
		const validStep = Math.max(1, Math.min(step, TOTAL_STEPS));

		const currentState = location.state as { redirect?: string } | undefined;
		navigate(`/account/transfer-points?step=${validStep}`, {
			replace: true,
			state: currentState,
		});
	};

	const nextStep = () => {
		const current = currentStep();
		if (current < TOTAL_STEPS) {
			goToStep(current + 1);
		}
	};

	const prevStep = () => {
		const current = currentStep();
		if (current > 1) {
			goToStep(current - 1);
		} else {
			navigate(getReturnUrl());
		}
	};

	const facilitiesData = createAsync(() =>
		cachedGet('/v0/facilities')({ query: { location: {}, supportsAccessPoints: true } }),
	);

	return (
		<>
			<Title>Transfer Points | Golf with Access</Title>
			<div
				class="fixed inset-0 h-svh w-svw overflow-hidden bg-white"
				classList={{ 'grid md:grid-cols-2': !STEPS[currentStep() - 1]?.fullWidth }}
			>
				<Show when={STEPS[currentStep() - 1]}>
					{(config) => (
						<Step
							config={config()}
							currentStep={currentStep()}
							onPrevStep={prevStep}
							onNextStep={nextStep}
							facilities={facilitiesData()?.facilities}
							confirmDialogOpen={confirmDialogOpen()}
							setConfirmDialogOpen={setConfirmDialogOpen}
							selectedOption={selectedOption()}
							setSelectedOption={setSelectedOption}
							conversionComplete={conversionComplete()}
							setConversionComplete={setConversionComplete}
						/>
					)}
				</Show>
			</div>
		</>
	);
}

function Step(props: {
	config: StepConfig;
	currentStep: number;
	onPrevStep: () => void;
	onNextStep: () => void;
	facilities?: Array<Facility>;
	confirmDialogOpen: boolean;
	setConfirmDialogOpen: (open: boolean) => void;
	selectedOption: 'primary' | 'upgrade' | null;
	setSelectedOption: (option: 'primary' | 'upgrade' | null) => void;
	conversionComplete: boolean;
	setConversionComplete: (complete: boolean) => void;
}) {
	const [dialogOpen, setDialogOpen] = createSignal(false);
	const rightContent = children(() => props.config.rightContent?.({ facilities: props.facilities }));

	return (
		<Show
			when={!props.config.fullWidth}
			fallback={
				<FullWidthLayout
					title={props.config.title}
					currentStep={props.currentStep}
					onPrevStep={props.onPrevStep}
					onNextStep={props.onNextStep}
					onConfirm={props.currentStep === TOTAL_STEPS ? () => props.setConfirmDialogOpen(true) : undefined}
					selectedOption={props.selectedOption}
					conversionComplete={props.conversionComplete}
				>
					{props.config.leftContent({
						confirmDialogOpen: props.confirmDialogOpen,
						setConfirmDialogOpen: props.setConfirmDialogOpen,
						selectedOption: props.selectedOption,
						setSelectedOption: props.setSelectedOption,
						setConversionComplete: props.setConversionComplete,
					})}
				</FullWidthLayout>
			}
		>
			<LeftLayout
				title={props.config.title}
				currentStep={props.currentStep}
				onPrevStep={props.onPrevStep}
				onNextStep={props.onNextStep}
				onConfirm={props.currentStep === TOTAL_STEPS ? () => props.setConfirmDialogOpen(true) : undefined}
				openDialog={props.config.dialogTitle ? () => setDialogOpen(true) : undefined}
				dialogTriggerLabel={props.config.dialogTitle}
			>
				{props.config.leftContent()}
			</LeftLayout>
			<Show when={rightContent()}>
				<RightLayout
					dialogOpen={dialogOpen()}
					closeDialog={() => setDialogOpen(false)}
					dialogTitle={props.config.dialogTitle}
				>
					{rightContent()}
				</RightLayout>
			</Show>
		</Show>
	);
}

function RightLayout(props: {
	children: JSX.Element;
	dialogTitle?: string;
	dialogOpen?: boolean;
	closeDialog?: () => void;
	class?: string;
}) {
	return (
		<>
			<Show when={props.dialogTitle}>
				<div class="md:hidden">
					<Dialog key={props.dialogTitle || ''} open={props.dialogOpen} onOpenChange={props.closeDialog}>
						<DialogContent header={props.dialogTitle} headerLevel="h2" width="lg" height="full" noPadding>
							<Show when={props.dialogOpen}>
								<div style={{ height: 'calc(97dvh)' }} class="w-full">
									{props.children}
								</div>
							</Show>
						</DialogContent>
					</Dialog>
				</div>
			</Show>
			<div class="hidden h-svh overflow-hidden md:block">{props.children}</div>
		</>
	);
}

function LeftLayout(props: {
	title?: string;
	currentStep: number;
	children: JSX.Element;
	openDialog?: () => void;
	dialogTriggerLabel?: string;
	onPrevStep: () => void;
	onNextStep: () => void;
	onConfirm?: () => void;
}) {
	const navigate = useNavigate();
	const location = useLocation();

	const getReturnUrl = (): string => {
		const redirect = (location.state as { redirect?: string } | undefined)?.redirect;
		if (redirect && !redirect.includes('/account/transfer-points')) {
			return redirect;
		}
		return '/account';
	};

	const handleCancel = () => {
		navigate(getReturnUrl());
	};

	const handleConfirm = () => {
		if (props.onConfirm) {
			props.onConfirm();
		} else {
			navigate(getReturnUrl());
		}
	};

	return (
		<div class="grid h-svh grid-rows-[auto_1fr_auto]">
			<div class="grid max-w-max p-4 md:p-6">
				<AccessLogo class="w-36" aria-label="Powered by Troon" />
			</div>

			<Section appearance="default" class="grid place-items-center overflow-y-auto px-6 md:px-8">
				<div class="grid w-full gap-4 py-6 md:max-w-xl md:py-8">
					<Heading as="h2" size="h6" class="font-light">
						{props.title}
					</Heading>
					{props.children}
					<Show when={props.dialogTriggerLabel}>
						<Button
							onClick={props.openDialog}
							appearance="transparent"
							class="max-w-max rounded-full bg-brand-100 md:hidden"
						>
							<span class="text-sm text-black">{props.dialogTriggerLabel}</span>
							<IconInfo class="text-brand" />
						</Button>
					</Show>
				</div>
			</Section>

			<div>
				<div class="px-6 pb-4 md:px-8">
					<div
						role="progressbar"
						aria-valuenow={props.currentStep}
						aria-valuemin={1}
						aria-valuemax={TOTAL_STEPS}
						aria-label={`Step ${props.currentStep} of ${TOTAL_STEPS}`}
						class="h-1 w-full overflow-hidden rounded-full bg-neutral-300"
					>
						<div
							class="h-full bg-brand transition-all duration-300"
							style={{ width: `${(props.currentStep / TOTAL_STEPS) * 100}%` }}
						/>
					</div>
				</div>

				{/* Navigation buttons */}
				<div class="flex items-center justify-between border-t border-neutral-300 bg-white p-4 md:px-6">
					<Show
						when={props.currentStep !== 1}
						fallback={
							<Button onClick={handleCancel} appearance="transparent" class="max-w-max">
								Cancel
							</Button>
						}
					>
						<div>
							<Button
								onClick={props.onPrevStep}
								appearance={props.currentStep === 1 ? 'transparent' : 'tertiary'}
								class="max-w-max"
							>
								<IconArrowLeftMd />
								<span class="sr-only md:not-sr-only">Back</span>
							</Button>
						</div>
					</Show>

					<Show
						when={props.currentStep !== TOTAL_STEPS}
						fallback={
							<Button onClick={handleConfirm} class="text-sm md:text-base">
								<span class="whitespace-nowrap">Confirm Transfer</span> <IconArrowRightMd />
							</Button>
						}
					>
						<div>
							<Button onClick={props.onNextStep} class="text-sm md:text-base">
								<span class="whitespace-nowrap">Continue</span> <IconArrowRightMd />
							</Button>
						</div>
					</Show>
				</div>
			</div>
		</div>
	);
}

function FullWidthLayout(props: {
	title?: string;
	currentStep: number;
	children: JSX.Element;
	onPrevStep: () => void;
	onNextStep: () => void;
	onConfirm?: () => void;
	selectedOption?: 'primary' | 'upgrade' | null;
	conversionComplete: boolean;
}) {
	const navigate = useNavigate();
	const location = useLocation();

	const getReturnUrl = (): string => {
		const redirect = (location.state as { redirect?: string } | undefined)?.redirect;
		if (redirect && !redirect.includes('/account/transfer-points')) {
			return redirect;
		}
		return '/account';
	};

	const handleConfirm = () => {
		if (props.onConfirm) {
			props.onConfirm();
		} else {
			navigate(getReturnUrl());
		}
	};

	const handleCancel = () => {
		navigate(getReturnUrl());
	};

	return (
		<div class="grid h-svh grid-rows-[auto_1fr_auto]">
			<div class="grid max-w-max p-4 md:p-6">
				<AccessLogo class="w-36" aria-label="Powered by Troon" />
			</div>
			<Section appearance="default" class="overflow-y-auto px-4 md:px-8">
				<div class="mx-auto w-full max-w-2xl py-6 md:py-8">
					<Show when={props.title}>
						<Heading as="h2" size="h3" class="mb-6">
							{props.title}
						</Heading>
					</Show>
					{props.children}
				</div>
			</Section>

			<Show when={!props.conversionComplete}>
				<div class="border-t border-neutral-400 bg-white">
					<div class="mx-auto flex max-w-3xl items-center justify-between p-4 md:p-6">
						<div>
							<Show
								when={props.currentStep !== 1}
								fallback={
									<Button onClick={handleCancel} appearance="transparent" class="max-w-max text-sm md:text-base">
										Cancel
									</Button>
								}
							>
								<Button
									onClick={props.onPrevStep}
									appearance={props.currentStep === 1 ? 'transparent' : 'tertiary'}
									class="max-w-max text-sm md:text-base"
								>
									<IconArrowLeftMd />
									<span class="sr-only md:not-sr-only">Back</span>
								</Button>
							</Show>
						</div>

						<div>
							<Show
								when={props.currentStep !== TOTAL_STEPS}
								fallback={
									<Button onClick={handleConfirm} disabled={!props.selectedOption} class="text-sm md:text-base">
										<span class="whitespace-nowrap">Confirm Transfer</span> <IconArrowRightMd />
									</Button>
								}
							>
								<Button onClick={props.onNextStep} class="text-sm md:text-base">
									<span class="whitespace-nowrap">Continue</span> <IconArrowRightMd />
								</Button>
							</Show>
						</div>
					</div>
				</div>
			</Show>
		</div>
	);
}

export const route = {
	info: { nav: { appearance: 'none' }, banner: { hide: true } },
	preload: () => redirectZeroPointsUsers(),
} satisfies RouteDefinition;
